import { useCompliance } from '@hooks/useCompliance';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * Encapsulates restart-onboarding state and handlers.
 *
 * @param {{ resetFlow: Function }} options
 * @returns {{
 *   showRestartModal: boolean,
 *   isRestarting: boolean,
 *   canRestart: boolean,
 *   setCanRestart: Function,
 *   handleBackClick: Function,
 *   handleRestart: Function,
 *   closeRestartModal: Function
 * }}
 */
/**
 * Determine if a KYC profile can be restarted.
 * Restart is allowed for any draft onboarding record, even before the
 * first section is saved, so the user can back out and pick a new path.
 * @param {object} profile - KYC profile from the API
 * @returns {boolean}
 */
const canRestartProfile = (profile) => {
  const status = profile?.status ?? profile?.kyc?.status;
  return status === 'DRAFT';
};

const useRestartOnboarding = ({ resetFlow }) => {
  const history = useHistory();
  const { restartKyc } = useCompliance();

  const [showRestartModal, setShowRestartModal] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [canRestart, setCanRestart] = useState(false);

  useEffect(() => {
    if (!canRestart || typeof history.block !== 'function') {
      return undefined;
    }

    const unblock = history.block((nextLocation, action) => {
      if (action === 'POP' && nextLocation?.pathname === '/onboard') {
        setShowRestartModal(true);
        return false;
      }

      return undefined;
    });

    return unblock;
  }, [canRestart, history]);

  const handleBackClick = useCallback(() => {
    if (canRestart) {
      setShowRestartModal(true);
    } else {
      history.push('/onboard');
    }
  }, [canRestart, history]);

  const handleRestart = useCallback(async () => {
    try {
      setIsRestarting(true);
      await restartKyc();
      resetFlow();
      setShowRestartModal(false);
      setIsRestarting(false);
      history.push('/onboard');
    } catch {
      setIsRestarting(false);
    }
  }, [restartKyc, resetFlow, history]);

  const closeRestartModal = useCallback(() => {
    setShowRestartModal(false);
  }, []);

  return {
    showRestartModal,
    isRestarting,
    canRestart,
    setCanRestart,
    handleBackClick,
    handleRestart,
    closeRestartModal
  };
};

export { canRestartProfile, useRestartOnboarding };
